import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {boolean} */
  #pagesFetched = false;

  /** @type {number | undefined} */
  #filterPrefetchTimer = 0;

  #shouldPrefetchPages() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection?.saveData) return false;

    const effectiveType = connection?.effectiveType;
    if (effectiveType === 'slow-2g' || effectiveType === '2g') return false;

    return true;
  }

  #scheduleIdlePrefetch(callback) {
    if (!this.#shouldPrefetchPages()) return;

    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(callback, { timeout: 4000 });
      return;
    }

    window.setTimeout(callback, 1500);
  }

  #getResultsURL() {
    const sourceURL = this.getAttribute('data-results-url');
    const url = new URL(sourceURL || window.location.href, window.location.origin);
    const currentURL = new URL(window.location.href);
    url.search = currentURL.search;
    url.hash = '';
    return url;
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.#pagesFetched) {
      this.#pagesFetched = true;
      this.#scheduleIdlePrefetch(() => {
        const paginationInfo = this.#getPaginationInfo();
        if (paginationInfo && paginationInfo.totalPages < 2) return;
        this.#fetchPage('next');
      });
    }
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearTimeout(this.#filterPrefetchTimer);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * @param {Object} pageInfo - The page info
   * @param {number} pageInfo.page - The page number
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;
    if (pageInfo.page < 1) return false;
    
    const paginationInfo = this.#getPaginationInfo();
    if (paginationInfo && pageInfo.page > paginationInfo.totalPages) return false;

    return true;
  }

  /**
   * Get pagination info from DOM
   * @returns {Object|null} Pagination info with totalPages
   */
  #getPaginationInfo() {
    const customPaginationElement = this.querySelector('.paginShop');
    if (customPaginationElement instanceof HTMLElement && customPaginationElement.dataset.paginationTotal) {
      const totalPages = parseInt(customPaginationElement.dataset.paginationTotal, 10);
      if (!Number.isNaN(totalPages) && totalPages > 0) {
        return { totalPages };
      }
    }

    const legacyPaginationElement = this.querySelector('.paginShopNum__all');
    if (legacyPaginationElement) {
      const totalPages = parseInt(legacyPaginationElement.textContent, 10);
      if (!Number.isNaN(totalPages) && totalPages > 0) {
        return { totalPages };
      }
    }
    
    // Fallback to Shopify's default pagination
    const shopifyPaginationElement = this.querySelector('.pagination__nav .pagination__nav-item:last-child');
    if (shopifyPaginationElement) {
      const totalPages = parseInt(shopifyPaginationElement.textContent);
      if (!isNaN(totalPages)) {
        return { totalPages };
      }
    }
    
    // Calculate based on current cards count
    const currentCards = this.querySelectorAll('[ref="cards[]"]');
    if (currentCards.length > 0) {
      const cardsPerPage = currentCards.length;
      
      const totalItemsElement = this.querySelector('.filters__count, .collection-mobile-controls__right');
      if (totalItemsElement) {
        const totalItemsText = totalItemsElement.textContent;
        const totalItems = parseInt(totalItemsText.match(/\d+/)?.[0] || '0');
        if (totalItems > 0) {
          const estimatedPages = Math.ceil(totalItems / cardsPerPage);
          return { totalPages: estimatedPages };
        }
      }
    }
    
    // If no pagination info found, don't block any pages
    return null;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    if (!page || !this.#shouldUsePage(page)) return;

    await this.#fetchSpecificPage(page.page, page.url);
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = this.#getResultsURL();
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const page = isPrevious ? Number(targetCard.dataset.page) - 1 : Number(targetCard.dataset.page) + 1;

    const url = this.#getResultsURL();
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    window.clearTimeout(this.#filterPrefetchTimer);
    this.#pagesFetched = false;
    this.pages.clear();

    this.#filterPrefetchTimer = window.setTimeout(() => {
      const paginationInfo = this.#getPaginationInfo();
      if (paginationInfo && paginationInfo.totalPages < 2) return;
      this.#scheduleIdlePrefetch(() => {
        this.#fetchSpecificPage(2);
      });
    }, 800);
  };
}
